import { Loader, CustomProvider } from 'rsuite'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { Page, Spacer } from '@/components/styles'
import Footer from '@/components/footer'
import Header from '@/components/header'
import ToasterProvider from '@/components/shared/toasterProvider'
import { customerId } from '@/utils/env'
import { customerHelper } from '@tweddle-se-team/common/constants'

const isNordics = customerId === customerHelper.CUSTOMER_IDS.NORDICS

const PagesProgressProvider = dynamic(() => import('@bprogress/next').then(mod => mod.PagesProgressProvider), {
  ssr: false
})

function ProgressBarWrapper({ children }) {
  return <PagesProgressProvider>{children}</PagesProgressProvider>
}

function MainPage({ children, loading }) {
  const router = useRouter()
  const pageRef = useRef(null)
  const previousPathnameRef = useRef(router.pathname)
  const hideFooter = isNordics && router.pathname.startsWith('/pdf')

  // Reset focus on route change for proper tab order
  // This ensures tab navigation starts from the beginning of the page
  useEffect(() => {
    const handleRouteChange = url => {
      // for /content page we need to reset focus on query param changes as well
      const newPathname = url.startsWith('/content') ? url : url.split('?')[0]

      // Only reset focus on actual pathname change, not just query params
      if (newPathname !== previousPathnameRef.current) {
        previousPathnameRef.current = newPathname

        setTimeout(() => {
          const pageContainer = pageRef.current
          if (pageContainer) {
            pageContainer.focus()
            pageContainer.blur()
          }
        }, 0)
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return (
    <ProgressBarWrapper>
      <CustomProvider>
        <ToasterProvider>
          <Page ref={pageRef} tabIndex={-1} style={{ outline: 'none' }}>
            <Header />
            <div id="main-content" tabIndex={-1} style={{ outline: 'none', margin: '3px 5px' }}>
              {loading && <Loader size="lg" backdrop center />}
              {!loading && children}
            </div>
            {!hideFooter && (
              <>
                <Spacer />
                <Footer />
              </>
            )}
          </Page>
        </ToasterProvider>
      </CustomProvider>
    </ProgressBarWrapper>
  )
}

export default MainPage
